/**
 * Type definitions for Kommunikation (Communication) data
 */

/**
 * Top channel item
 */
export interface KommunikationTopKanal {
  Position: number;
  Name: string;
  Wirkungsgrad: number;
  Angesprochenes_Milieu: string;
}

/**
 * Scatter chart data point
 */
export interface ScatterDataPoint {
  name: string;
  x_value: number;
  y_value: number;
}

/**
 * Axis encoding for charts
 */
export interface AxisEncoding {
  label: string;
  unit: number;
  increments: number;
  start: number;
  end: number;
}

/**
 * Produkt-Fit-Matrix (Scatter chart)
 */
export interface KommunikationProduktFitMatrix {
  diagram: string;
  type: 'scatter';
  encoding: {
    x: AxisEncoding;
    y: AxisEncoding;
  };
  data: ScatterDataPoint[];
  notes: {
    interpretation_hint: string;
  };
}

/**
 * Feature for heatmap axes
 */
export interface HeatmapFeature {
  name: string;
  index: number;
}

/**
 * Heatmap data point
 */
export interface HeatmapDataPoint {
  x_value: number;
  y_value: number;
  value: number;
}

/**
 * Kanal-Milieu-Zuordnung (Heatmap)
 */
export interface KommunikationKanalMilieuZuordnung {
  diagram: string;
  type: 'heatmap';
  encoding: {
    x: {
      label: string;
      features: HeatmapFeature[];
    };
    y: {
      label: string;
      features: HeatmapFeature[];
    };
    scale: {
      label: string;
      unit: number;
      increments: number;
      start: number;
      end: number;
    };
  };
  data: HeatmapDataPoint[];
  notes: {
    interpretation_hint: string;
  };
}

/**
 * Bubble scatter data point
 */
export interface BubbleScatterDataPoint {
  name: string;
  x_value: number;
  y_value: number;
}

/**
 * Kosten-Nutzen-Vergleich (Bubble scatter)
 */
export interface KommunikationKostenNutzenVergleich {
  diagram: string;
  type: 'bubble_scatter';
  encoding: {
    x: AxisEncoding;
    y: AxisEncoding;
  };
  data: BubbleScatterDataPoint[];
  notes: {
    interpretation_hint: string;
  };
}

/**
 * Radar feature
 */
export interface RadarFeature {
  feature_index: number;
  name: string;
}

/**
 * Radar dimension data
 */
export interface RadarDimension {
  feature_index: number;
  value: number;
}

/**
 * Radar data point
 */
export interface RadarDataPoint {
  kanal: string;
  dimensions: RadarDimension[];
}

/**
 * Kanal-Reifegrad-Radar
 */
export interface KommunikationKanalReifegradRadar {
  diagram: string;
  type: 'radar';
  encoding: {
    features: RadarFeature[];
  };
  data: RadarDataPoint[];
  notes: {
    interpretation_hint: string;
    value_range: [number, number];
  };
}

/**
 * Main Kommunikation data structure from API
 */
export interface KommunikationData {
  Kommunikation_Top_Kanäle: KommunikationTopKanal[];
  Kommunikation_Produkt_Fit_Matrix: KommunikationProduktFitMatrix;
  'Kommunikation_Kanal-Milieu-Zuordnung': KommunikationKanalMilieuZuordnung;
  'Kommunikation_Kosten-Nutzen-Vergleich': KommunikationKostenNutzenVergleich;
  'Kommunikation_Kanal-Reifegrad-Radar': KommunikationKanalReifegradRadar;
  Kommunikation_Zusammenfassung: string;
}

/**
 * Combined data structure for card and overlay
 */
export interface KommunikationFullData {
  main: KommunikationData;
}

/**
 * Get effectiveness color based on value (1-10 scale)
 */
export function getEffectivenessColor(value: number): string {
  if (value >= 8) return '#22c55e'; // green-500
  if (value >= 6) return '#84cc16'; // lime-500
  if (value >= 4) return '#eab308'; // yellow-500
  if (value >= 2) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
}

/**
 * Parse raw JSON string from API into structured Kommunikation data
 */
export function parseKommunikationData(jsonString: string): KommunikationFullData | null {
  try {
    let data = jsonString;

    // If the string is wrapped in quotes, try to parse it first
    if (typeof data === 'string' && (data.startsWith('"') || data.startsWith("'"))) {
      try {
        data = JSON.parse(data);
      } catch {
        // If it fails, continue with original string
      }
    }

    const parsed = typeof data === 'string' ? JSON.parse(data) : data;

    // Handle case where it's an array
    const mainData = Array.isArray(parsed) ? parsed[0] : parsed;

    if (!mainData) {
      return null;
    }

    return {
      main: mainData as KommunikationData,
    };
  } catch {
    return null;
  }
}

/**
 * Generate mock data for development
 */
export function getMockKommunikationData(): KommunikationFullData {
  const mockData: KommunikationData = {
    Kommunikation_Top_Kanäle: [
      { Position: 1, Name: 'LinkedIn', Wirkungsgrad: 9, Angesprochenes_Milieu: 'Digital affin' },
      { Position: 2, Name: 'E-Mail Marketing', Wirkungsgrad: 8, Angesprochenes_Milieu: 'Pragmatisch' },
      { Position: 3, Name: 'Webseite', Wirkungsgrad: 7, Angesprochenes_Milieu: 'Innovation' },
      { Position: 4, Name: 'Direktvertrieb', Wirkungsgrad: 6, Angesprochenes_Milieu: 'Sicherheit' },
      { Position: 5, Name: 'Messen & Events', Wirkungsgrad: 5, Angesprochenes_Milieu: 'Pragmatisch' },
    ],
    Kommunikation_Produkt_Fit_Matrix: {
      diagram: 'Kanal-Produkt-Fit-Matrix',
      type: 'scatter',
      encoding: {
        x: { label: 'produkt_fit', unit: 1, increments: 1, start: 1, end: 10 },
        y: { label: 'ansprache_effektivitaet', unit: 1, increments: 1, start: 1, end: 10 },
      },
      data: [
        { name: 'LinkedIn', x_value: 8.5, y_value: 9.0 },
        { name: 'E-Mail', x_value: 7.5, y_value: 8.0 },
        { name: 'Webseite', x_value: 6.5, y_value: 7.0 },
        { name: 'Direktvertrieb', x_value: 8.0, y_value: 6.0 },
        { name: 'Messen', x_value: 5.0, y_value: 5.5 },
      ],
      notes: { interpretation_hint: 'Rechts-oben = hoher Produktfit und hohe Wirksamkeit.' },
    },
    'Kommunikation_Kanal-Milieu-Zuordnung': {
      diagram: 'Kanal-Milieu-Zuordnung',
      type: 'heatmap',
      encoding: {
        x: {
          label: 'kanal',
          features: [
            { name: 'Webseite', index: 1 },
            { name: 'LinkedIn', index: 2 },
            { name: 'E-Mail', index: 3 },
            { name: 'Direktvertrieb', index: 4 },
          ],
        },
        y: {
          label: 'milieu',
          features: [
            { name: 'Innovation', index: 1 },
            { name: 'Sicherheit', index: 2 },
            { name: 'Pragmatisch', index: 3 },
            { name: 'Digital affin', index: 4 },
          ],
        },
        scale: { label: 'relevanz', unit: 1, increments: 1, start: 1, end: 10 },
      },
      data: [
        { x_value: 1, y_value: 1, value: 7 },
        { x_value: 1, y_value: 2, value: 5 },
        { x_value: 1, y_value: 3, value: 6 },
        { x_value: 1, y_value: 4, value: 8 },
        { x_value: 2, y_value: 1, value: 8 },
        { x_value: 2, y_value: 2, value: 4 },
        { x_value: 2, y_value: 3, value: 6 },
        { x_value: 2, y_value: 4, value: 9 },
        { x_value: 3, y_value: 1, value: 5 },
        { x_value: 3, y_value: 2, value: 7 },
        { x_value: 3, y_value: 3, value: 8 },
        { x_value: 3, y_value: 4, value: 6 },
        { x_value: 4, y_value: 1, value: 4 },
        { x_value: 4, y_value: 2, value: 9 },
        { x_value: 4, y_value: 3, value: 7 },
        { x_value: 4, y_value: 4, value: 3 },
      ],
      notes: { interpretation_hint: 'Höhere Relevanz = bessere Eignung des Kanals für das Milieu.' },
    },
    'Kommunikation_Kosten-Nutzen-Vergleich': {
      diagram: 'Kosten-Nutzen-Vergleich der Kanäle',
      type: 'bubble_scatter',
      encoding: {
        x: { label: 'kosten_pro_lead_eur', unit: 1, increments: 1, start: 1, end: 10 },
        y: { label: 'abschlussquote', unit: 1, increments: 1, start: 1, end: 10 },
      },
      data: [
        { name: 'Website', x_value: 3, y_value: 6 },
        { name: 'LinkedIn', x_value: 5, y_value: 8 },
        { name: 'E-Mail', x_value: 2, y_value: 7 },
        { name: 'Direktvertrieb', x_value: 8, y_value: 9 },
        { name: 'Messen', x_value: 9, y_value: 5 },
      ],
      notes: { interpretation_hint: 'Oben-links = günstig und effektiv; Bubble-Größe zeigt Potenzial/Skalierung.' },
    },
    'Kommunikation_Kanal-Reifegrad-Radar': {
      diagram: 'Kanal-Reifegrad-Radar',
      type: 'radar',
      encoding: {
        features: [
          { feature_index: 1, name: 'Vertrauen' },
          { feature_index: 2, name: 'Sicherheit' },
          { feature_index: 3, name: 'Günstig' },
          { feature_index: 4, name: 'Einfach' },
          { feature_index: 5, name: 'Regional' },
        ],
      },
      data: [
        {
          kanal: 'LinkedIn',
          dimensions: [
            { feature_index: 1, value: 4 },
            { feature_index: 2, value: 3 },
            { feature_index: 3, value: 3 },
            { feature_index: 4, value: 5 },
            { feature_index: 5, value: 2 },
          ],
        },
        {
          kanal: 'E-Mail',
          dimensions: [
            { feature_index: 1, value: 3 },
            { feature_index: 2, value: 4 },
            { feature_index: 3, value: 5 },
            { feature_index: 4, value: 4 },
            { feature_index: 5, value: 3 },
          ],
        },
        {
          kanal: 'Direktvertrieb',
          dimensions: [
            { feature_index: 1, value: 5 },
            { feature_index: 2, value: 5 },
            { feature_index: 3, value: 2 },
            { feature_index: 4, value: 2 },
            { feature_index: 5, value: 5 },
          ],
        },
      ],
      notes: {
        interpretation_hint: 'Höherer Score = stärkerer Reifegrad in der Dimension.',
        value_range: [1, 5],
      },
    },
    Kommunikation_Zusammenfassung: 'Die Kommunikationsanalyse zeigt, dass LinkedIn und E-Mail Marketing die effektivsten Kanäle für Ihre Zielgruppe sind. LinkedIn erreicht besonders gut digital affine Milieus mit einem hohen Wirkungsgrad von 9/10. E-Mail Marketing bietet ein optimales Kosten-Nutzen-Verhältnis und spricht pragmatische Zielgruppen effektiv an. Für die Ansprache sicherheitsorientierter Milieus empfehlen wir den Direktvertrieb, der trotz höherer Kosten die beste Vertrauensbasis schafft.',
  };

  return {
    main: mockData,
  };
}
